import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../utils/firebase';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import HostLayout from '../layouts/HostLayout';
import { useParams } from 'react-router-dom';
import { checkHostAchievements, getHostAchievementInfo, checkHostFounder, checkHostFoundersClub, checkCompanyFounder } from '../utils/hostAchievements';
import BadgeDisplay from '../components/BadgeDisplay';

const HostProfile = () => {
  const { user } = useAuth();
  const { hostId } = useParams(); // For viewing other hosts' profiles
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    contactEmail: '',
    contactPhone: ''
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [venuePredictions, setVenuePredictions] = useState([]);
  const [showVenuePredictions, setShowVenuePredictions] = useState(false);
  const [venueLoading, setVenueLoading] = useState(false);
  const [editingGameIndex, setEditingGameIndex] = useState(null);
  const [editingGameType, setEditingGameType] = useState(null);
  const autocompleteService = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    bio: '',
    phoneNumber: '',
    phoneNumberPublic: false,
    emailPublic: false,
    location: '',
    hostingExperience: '',
    preferredCategories: [],
    profileImageUrl: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      tiktok: '',
      twitter: '',
      spotify: '',
      pandora: ''
    },
    availability: {
      monday: { available: false, times: [] },
      tuesday: { available: false, times: [] },
      wednesday: { available: false, times: [] },
      thursday: { available: false, times: [] },
      friday: { available: false, times: [] },
      saturday: { available: false, times: [] },
      sunday: { available: false, times: [] }
    },
    currentGames: [],
    availabilityNotes: ''
  });

  // Initialize Google Places services
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      // Use the newer AutocompleteSuggestion instead of deprecated AutocompleteService
      autocompleteService.current = window.google.maps.places.AutocompleteSuggestion;
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const targetUserId = hostId || user?.uid;
      if (!targetUserId) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', targetUserId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFormData({
            fullName: data.fullName || '',
            bio: data.bio || '',
            phoneNumber: data.phoneNumber || '',
            phoneNumberPublic: data.phoneNumberPublic || false,
            emailPublic: data.emailPublic || false,
            location: data.location || '',
            hostingExperience: data.hostingExperience || '',
            preferredCategories: data.preferredCategories || [],
            profileImageUrl: data.profileImageUrl || '',
            socialMedia: data.socialMedia || {
              instagram: '',
              facebook: '',
              tiktok: '',
              twitter: '',
              spotify: '',
              pandora: ''
            },
            availability: data.availability || {
              monday: { available: false, times: [] },
              tuesday: { available: false, times: [] },
              wednesday: { available: false, times: [] },
              thursday: { available: false, times: [] },
              friday: { available: false, times: [] },
              saturday: { available: false, times: [] },
              sunday: { available: false, times: [] }
            },
            currentGames: data.currentGames || [],
            availabilityNotes: data.availabilityNotes || ''
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, hostId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const handleTimeSlotChange = (day, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          times: prev.availability[day].times.map((time, i) => 
            i === index ? { ...time, [field]: value } : time
          )
        }
      }
    }));
  };

  const addTimeSlot = (day) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          times: [...prev.availability[day].times, { start: '', end: '' }]
        }
      }
    }));
  };

  const removeTimeSlot = (day, index) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          times: prev.availability[day].times.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const addGame = () => {
    const newGame = {
      id: Date.now(),
      title: '',
      venue: '',
      day: '',
      time: '',
      description: '',
      recurring: false
    };
    
    setFormData(prev => ({
      ...prev,
      currentGames: [...prev.currentGames, newGame]
    }));
  };

  const updateGame = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      currentGames: prev.currentGames.map((game, i) => 
        i === index ? { ...game, [field]: value } : game
      )
    }));
  };

  const removeGame = (index) => {
    setFormData(prev => ({
      ...prev,
      currentGames: prev.currentGames.filter((_, i) => i !== index)
    }));
  };



  // Venue autocomplete functions
  const handleVenueSearch = async (value, gameIndex) => {
    setEditingGameIndex(gameIndex);
    setEditingGameType('currentGames');
    
    if (!value.trim() || !autocompleteService.current) {
      setVenuePredictions([]);
      setShowVenuePredictions(false);
      return;
    }

    setVenueLoading(true);
    
    try {
      const request = {
        input: value,
        types: ['establishment'],
        componentRestrictions: { country: 'us' }
      };

      const response = await autocompleteService.current.getPlacePredictions(request);
      setVenueLoading(false);
      
      if (response && response.predictions) {
        setVenuePredictions(response.predictions);
        setShowVenuePredictions(true);
      } else {
        setVenuePredictions([]);
        setShowVenuePredictions(false);
      }
    } catch (error) {
      console.error('Error fetching venue predictions:', error);
      setVenueLoading(false);
      setVenuePredictions([]);
      setShowVenuePredictions(false);
    }
  };

  const handleVenueSelect = async (prediction, gameIndex) => {
    try {
      // Use the prediction data directly instead of making additional API calls
      // This avoids the deprecated PlacesService entirely
      const venueName = prediction.structured_formatting.main_text;
      const venueAddress = prediction.structured_formatting.secondary_text;
      
      // Combine name and address for a more complete venue description
      const fullVenueName = venueAddress ? `${venueName}, ${venueAddress}` : venueName;
      
      updateGame(gameIndex, 'venue', fullVenueName);
      setShowVenuePredictions(false);
      setVenuePredictions([]);
      setEditingGameIndex(null);
      setEditingGameType(null);
    } catch (error) {
      console.error('Error setting venue:', error);
      // Fallback: use just the main text if there's an error
      const venueName = prediction.structured_formatting.main_text;
      updateGame(gameIndex, 'venue', venueName);
      setShowVenuePredictions(false);
      setVenuePredictions([]);
      setEditingGameIndex(null);
      setEditingGameType(null);
    }
  };

  const handleVenueInputFocus = () => {
    if (venuePredictions.length > 0) {
      setShowVenuePredictions(true);
    }
  };

  const handleVenueInputBlur = () => {
    // Delay hiding predictions to allow for clicks
    setTimeout(() => {
      setShowVenuePredictions(false);
    }, 200);
  };

  const handleSave = async () => {
    if (!user) return;
    
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, formData);
      setUserData(prev => ({ ...prev, ...formData }));
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleSendMessage = async () => {
    if (!user || !hostId) return;
    
    setSendingMessage(true);
    try {
      await addDoc(collection(db, 'hostMessages'), {
        fromUserId: user.uid,
        fromUserEmail: user.email,
        toHostId: hostId,
        toHostName: userData.fullName,
        subject: messageData.subject,
        message: messageData.message,
        contactEmail: messageData.contactEmail,
        contactPhone: messageData.contactPhone,
        createdAt: serverTimestamp(),
        status: 'unread'
      });
      
      setMessageData({
        subject: '',
        message: '',
        contactEmail: '',
        contactPhone: ''
      });
      setShowMessageForm(false);
      alert('Message sent successfully! The host will be notified.');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const checkAndAwardAchievements = useCallback(async () => {
    if (!userData || !user) return;
    
    try {
      const newAchievements = checkHostAchievements(userData);
      
      // Check for Founder badge
      if (checkHostFounder(user.uid) && !userData.achievements?.includes('host_founder')) {
        newAchievements.push('host_founder');
      }
      
      // Check for Company Founder badge
      if (checkCompanyFounder(user.uid, user.email) && !userData.achievements?.includes('company_founder')) {
        newAchievements.push('company_founder');
      }
      
      // Check for Founder's Club badge
      if (userData.achievements?.includes('host_founders_club') === false) {
        const isFounder = await checkHostFoundersClub(user.uid, db);
        if (isFounder) {
          newAchievements.push('host_founders_club');
        }
      }
      
      if (newAchievements.length > 0) {
        // Update user with new achievements
        const updatedAchievements = [...(userData.achievements || []), ...newAchievements];
        await updateDoc(doc(db, 'users', user.uid), {
          achievements: updatedAchievements
        });
        
        // Update local state
        setUserData(prev => ({ ...prev, achievements: updatedAchievements }));
        
        // Show notification for new achievements
        newAchievements.forEach(achievementId => {
          const achievement = getHostAchievementInfo(achievementId);
          if (achievement) {
            alert(`🎉 Achievement Unlocked: ${achievement.name} - ${achievement.description}`);
          }
        });
      }
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  }, [userData, user]);

  useEffect(() => {
    if (userData && user) {
      checkAndAwardAchievements();
    }
  }, [userData, user, checkAndAwardAchievements]);

  const triviaCategories = [
    'General Knowledge', 'History', 'Science', 'Geography', 'Sports',
    'Entertainment', 'Music', 'Movies', 'TV Shows', 'Literature',
    'Art', 'Technology', 'Food & Drink', 'Travel', 'Business',
    'Politics', 'Religion', 'Mythology', 'Animals', 'Nature'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Determine if this is the user's own profile or viewing another host's profile
  const isOwnProfile = !hostId || hostId === user?.uid;
  
  console.log('HostProfile Debug:', {
    hostId,
    userId: user?.uid,
    isOwnProfile,
    user: !!user
  });

  return (
    <HostLayout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Wallet Display removed - now shown in header */}
        
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">
            {isOwnProfile ? 'My Host Profile' : `${userData?.fullName || 'Host'}'s Profile`}
          </h1>
          {isOwnProfile ? (
            !editing ? (
              <button
                onClick={() => setEditing(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
              >
                Edit Profile
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormData({
                      fullName: userData.fullName || '',
                      bio: userData.bio || '',
                      phoneNumber: userData.phoneNumber || '',
                      phoneNumberPublic: userData.phoneNumberPublic || false,
                      emailPublic: userData.emailPublic || false,
                      location: userData.location || '',
                      hostingExperience: userData.hostingExperience || '',
                      preferredCategories: userData.preferredCategories || [],
                      profileImageUrl: userData.profileImageUrl || '',
                      socialMedia: userData.socialMedia || {
                        instagram: '',
                        facebook: '',
                        tiktok: '',
                        twitter: '',
                        spotify: '',
                        pandora: ''
                      },
                      availability: userData.availability || {
                        monday: { available: false, times: [] },
                        tuesday: { available: false, times: [] },
                        wednesday: { available: false, times: [] },
                        thursday: { available: false, times: [] },
                        friday: { available: false, times: [] },
                        saturday: { available: false, times: [] },
                        sunday: { available: false, times: [] }
                      },
                      currentGames: userData.currentGames || [],
                      availabilityNotes: userData.availabilityNotes || ''
                    });
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            )
          ) : (
            <div className="flex gap-3">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-400 text-sm">
                  Tips are not available in the current system. All payments go through Stripe for marketplace transactions.
                </p>
              </div>
              <button
                onClick={() => setShowMessageForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
              >
                Message Host
              </button>
            </div>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          {/* Profile Image Section */}
          <div className="flex items-start gap-6 mb-8">
            <div className="flex-shrink-0">
              {formData.profileImageUrl ? (
                <img
                  src={formData.profileImageUrl}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-600"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-700 border-4 border-gray-600 flex items-center justify-center text-4xl text-gray-400">
                  👤
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">
                {editing ? (
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    placeholder="Full Name"
                  />
                ) : (
                  formData.fullName || 'Host Name'
                )}
              </h2>
              
              {/* Host Statistics - Moved up next to profile picture */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-500">{userData?.gamesHosted || 0}</div>
                  <div className="text-gray-400 text-xs">Games Hosted</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-500">{userData?.averageRating?.toFixed(1) || 'N/A'}</div>
                  <div className="text-gray-400 text-xs">Avg Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-500">{userData?.achievements?.length || 0}</div>
                  <div className="text-gray-400 text-xs">Badges Earned</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-500">{userData?.bookingInquiries || 0}</div>
                  <div className="text-gray-400 text-xs">Booking Inquiries</div>
                </div>
              </div>
              
              {/* Achievements Section - Moved up next to profile picture */}
              <div className="mb-4">
                <BadgeDisplay 
                  achievements={userData?.achievements}
                  getAchievementInfo={getHostAchievementInfo}
                  title="Host Achievements"
                  emptyMessage="No achievements yet. Keep hosting to earn badges!"
                />
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bio */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              {editing ? (
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white h-24 resize-none"
                  placeholder="Tell us about yourself and your hosting style..."
                />
              ) : (
                <p className="text-gray-300 bg-gray-700 rounded px-3 py-2 min-h-[6rem]">
                  {formData.bio || 'No bio added yet.'}
                </p>
              )}
            </div>

            {/* Contact Information - Only show in edit mode or own profile */}
            {isOwnProfile && (
              <>
                {editing ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <div className="space-y-2">
                        <input
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                          placeholder="Phone Number (optional)"
                        />
                        <label className="flex items-center space-x-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.phoneNumberPublic}
                            onChange={(e) => handleInputChange('phoneNumberPublic', e.target.checked)}
                            className="rounded border-gray-600 bg-gray-700 text-green-500"
                          />
                          <span>Make phone number public on my profile</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                        placeholder="City, State"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {formData.phoneNumberPublic && formData.phoneNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <div className="bg-gray-700 rounded px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-300">
                              {formData.phoneNumber}
                            </span>
                            <span className="text-xs text-green-400 bg-green-900 px-2 py-1 rounded">
                              Public
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={formData.phoneNumberPublic && formData.phoneNumber ? "" : "md:col-span-2"}>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Location
                      </label>
                      <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                        {formData.location || 'Not specified'}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Public Contact Information - Show for all profiles */}
            {!isOwnProfile && ((userData?.phoneNumberPublic && userData?.phoneNumber) || (userData?.emailPublic && userData?.email)) && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Information
                </label>
                <div className="bg-gray-700 rounded px-3 py-2 space-y-2">
                  {userData?.phoneNumberPublic && userData?.phoneNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">📞</span>
                      <span className="text-gray-300">{userData.phoneNumber}</span>
                    </div>
                  )}
                  {userData?.emailPublic && userData?.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-400">✉️</span>
                      <span className="text-gray-300">{userData.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hosting Experience */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Hosting Experience
              </label>
              {editing ? (
                <select
                  value={formData.hostingExperience}
                  onChange={(e) => handleInputChange('hostingExperience', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  <option value="">Select experience level</option>
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (1-3 years)</option>
                  <option value="experienced">Experienced (3-5 years)</option>
                  <option value="expert">Expert (5+ years)</option>
                </select>
              ) : (
                <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                  {formData.hostingExperience ? 
                    formData.hostingExperience.charAt(0).toUpperCase() + formData.hostingExperience.slice(1) : 
                    'Not specified'
                  }
                </p>
              )}
            </div>

            {/* Preferred Categories */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Preferred Trivia Categories
              </label>
              {editing ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {triviaCategories.map(category => (
                    <label key={category} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.preferredCategories.includes(category)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...formData.preferredCategories, category]
                            : formData.preferredCategories.filter(c => c !== category);
                          handleInputChange('preferredCategories', updated);
                        }}
                        className="rounded border-gray-600 bg-gray-700 text-green-500"
                      />
                      <span className="text-sm text-gray-300">{category}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-700 rounded px-3 py-2">
                  {formData.preferredCategories.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.preferredCategories.map(category => (
                        <span key={category} className="bg-green-600 text-white px-2 py-1 rounded text-sm">
                          {category}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">No preferred categories selected</p>
                  )}
                </div>
              )}
            </div>

            {/* Social Media Links */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Social Media Links
              </label>
              <p className="text-xs text-gray-400 mb-3">(Not required, but encouraged for brand building and booking more shows.)</p>
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Instagram</label>
                    <input
                      type="url"
                      value={formData.socialMedia.instagram}
                      onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://instagram.com/username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Facebook</label>
                    <input
                      type="url"
                      value={formData.socialMedia.facebook}
                      onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://facebook.com/username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">TikTok</label>
                    <input
                      type="url"
                      value={formData.socialMedia.tiktok}
                      onChange={(e) => handleSocialMediaChange('tiktok', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://tiktok.com/@username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Twitter/X</label>
                    <input
                      type="url"
                      value={formData.socialMedia.twitter}
                      onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://twitter.com/username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Spotify</label>
                    <input
                      type="url"
                      value={formData.socialMedia.spotify}
                      onChange={(e) => handleSocialMediaChange('spotify', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://open.spotify.com/artist/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Pandora</label>
                    <input
                      type="url"
                      value={formData.socialMedia.pandora}
                      onChange={(e) => handleSocialMediaChange('pandora', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                      placeholder="https://pandora.com/station/..."
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-700 rounded px-3 py-2">
                  {Object.entries(formData.socialMedia).some(([platform, value]) => value.trim()) ? (
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(formData.socialMedia).map(([platform, value]) => {
                        if (!value.trim()) return null;
                        
                        const platformIcons = {
                          instagram: '📷',
                          facebook: '📘',
                          tiktok: '🎵',
                          twitter: '🐦',
                          spotify: '🎧',
                          pandora: '🎼'
                        };
                        
                        const platformNames = {
                          instagram: 'Instagram',
                          facebook: 'Facebook',
                          tiktok: 'TikTok',
                          twitter: 'Twitter/X',
                          spotify: 'Spotify',
                          pandora: 'Pandora'
                        };
                        
                        return (
                          <a
                            key={platform}
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gray-600 hover:bg-gray-500 rounded px-3 py-2 transition-colors duration-200 group"
                          >
                            <span className="text-xl">{platformIcons[platform]}</span>
                            <span className="text-sm text-gray-300 group-hover:text-white">{platformNames[platform]}</span>
                            <span className="text-xs text-green-400 group-hover:text-green-300">Visit Profile</span>
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No social media links added</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Availability Section - Only show in own profile */}
          {isOwnProfile && (
            <div className="mt-8 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Availability & Schedule</h3>
              <p className="text-xs text-gray-400 mb-4">Set your availability to help venues and teams find you for bookings.</p>
              
              {editing ? (
                <div className="space-y-6">
                  {/* Weekly Availability */}
                  <div>
                    <h4 className="text-md font-medium text-gray-300 mb-3">Weekly Availability</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(formData.availability).map(([day, dayData]) => (
                        <div key={day} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                          <div className="flex items-center justify-between mb-3">
                            <label className="flex items-center space-x-2 text-sm font-medium text-gray-300">
                              <input
                                type="checkbox"
                                checked={dayData.available}
                                onChange={(e) => handleAvailabilityChange(day, 'available', e.target.checked)}
                                className="rounded border-gray-600 bg-gray-700 text-green-500"
                              />
                              <span className="capitalize">{day}</span>
                            </label>
                            {dayData.available && (
                              <button
                                onClick={() => addTimeSlot(day)}
                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded"
                              >
                                + Add Time
                              </button>
                            )}
                          </div>
                          
                          {dayData.available && (
                            <div className="space-y-2">
                              {dayData.times.map((time, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <input
                                    type="time"
                                    value={time.start}
                                    onChange={(e) => handleTimeSlotChange(day, index, 'start', e.target.value)}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                                  />
                                  <span className="text-gray-400">to</span>
                                  <input
                                    type="time"
                                    value={time.end}
                                    onChange={(e) => handleTimeSlotChange(day, index, 'end', e.target.value)}
                                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                                  />
                                  <button
                                    onClick={() => removeTimeSlot(day, index)}
                                    className="text-red-400 hover:text-red-300 text-sm"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Availability Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Availability Notes</label>
                    <textarea
                      value={formData.availabilityNotes}
                      onChange={(e) => handleInputChange('availabilityNotes', e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white h-20 resize-none"
                      placeholder="Any additional notes about your availability, preferred booking times, or special requirements..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Display Availability */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(formData.availability).map(([day, dayData]) => (
                      dayData.available && (
                        <div key={day} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                          <h4 className="text-sm font-medium text-gray-300 mb-2 capitalize">{day}</h4>
                          {dayData.times.length > 0 ? (
                            <div className="space-y-1">
                              {dayData.times.map((time, index) => (
                                <div key={index} className="text-sm text-gray-400">
                                  {time.start} - {time.end}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">Available all day</div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                  
                  {formData.availabilityNotes && (
                    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-sm font-medium text-gray-300 mb-2">Notes</h4>
                      <p className="text-sm text-gray-400">{formData.availabilityNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Current Games Section */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Current Games</h3>
            {editing ? (
              <div className="space-y-4">
                {formData.currentGames.map((game, index) => (
                  <div key={game.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Game Title</label>
                        <input
                          type="text"
                          value={game.title}
                          onChange={(e) => updateGame(index, 'title', e.target.value)}
                          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                          placeholder="e.g., Tuesday Night Trivia"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Venue</label>
                        <div className="relative">
                          <input
                            type="text"
                            value={game.venue}
                            onChange={async (e) => {
                              updateGame(index, 'venue', e.target.value);
                              await handleVenueSearch(e.target.value, index);
                            }}
                            onFocus={handleVenueInputFocus}
                            onBlur={handleVenueInputBlur}
                            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm pr-8"
                            placeholder="Search for a venue..."
                          />
                          {venueLoading && editingGameIndex === index && editingGameType === 'currentGames' && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
                            </div>
                          )}
                        </div>
                        
                        {/* Venue Predictions Dropdown */}
                        {showVenuePredictions && venuePredictions.length > 0 && editingGameIndex === index && editingGameType === 'currentGames' && (
                          <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {venuePredictions.map((prediction) => (
                              <button
                                key={prediction.place_id}
                                onClick={() => handleVenueSelect(prediction, index)}
                                className="w-full text-left p-3 hover:bg-gray-700 text-white border-b border-gray-600 last:border-b-0 transition-colors"
                              >
                                <div className="font-medium">{prediction.structured_formatting.main_text}</div>
                                <div className="text-sm text-gray-400">{prediction.structured_formatting.secondary_text}</div>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* No Results */}
                        {showVenuePredictions && venuePredictions.length === 0 && game.venue.trim() && !venueLoading && editingGameIndex === index && editingGameType === 'currentGames' && (
                          <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3">
                            <div className="text-gray-400 text-center">No venues found</div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Day</label>
                        <select
                          value={game.day}
                          onChange={(e) => updateGame(index, 'day', e.target.value)}
                          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                        >
                          <option value="">Select day</option>
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Time</label>
                        <input
                          type="time"
                          value={game.time}
                          onChange={(e) => updateGame(index, 'time', e.target.value)}
                          className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
                      <textarea
                        value={game.description}
                        onChange={(e) => updateGame(index, 'description', e.target.value)}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-16 resize-none"
                        placeholder="Brief description of the game..."
                      />
                    </div>
                    <div className="mt-3">
                      <label className="flex items-center space-x-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={game.recurring || false}
                          onChange={(e) => updateGame(index, 'recurring', e.target.checked)}
                          className="rounded border-gray-600 bg-gray-700 text-green-500"
                        />
                        <span>This game recurs every week at the same venue, day, and time</span>
                      </label>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => removeGame(index)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove Game
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => addGame()}
                  className="w-full bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg p-4 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  + Add Current Game
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.currentGames.length > 0 ? (
                  formData.currentGames.map((game, index) => (
                    <div key={game.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-lg font-medium text-white">{game.title}</h4>
                        <div className="flex gap-2">
                          <span className="text-sm text-green-400 bg-green-900 px-2 py-1 rounded">Current</span>
                          {game.recurring && (
                            <span className="text-sm text-blue-400 bg-blue-900 px-2 py-1 rounded">Weekly</span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Venue:</span>
                          <span className="text-gray-300 ml-2">{game.venue}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Schedule:</span>
                          <span className="text-gray-300 ml-2">{game.day}s at {game.time}</span>
                        </div>
                      </div>
                      {game.description && (
                        <p className="text-sm text-gray-400 mt-2">{game.description}</p>
                      )}
                      {game.recurring && (
                        <p className="text-sm text-blue-400 mt-2">🔄 Recurring weekly game</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-center py-8">No current games listed</p>
                )}
              </div>
            )}
          </div>



          {/* Account Information (Read-only) - Only show in own profile */}
          {isOwnProfile && (
            <div className="mt-8 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {editing ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                      <div className="space-y-2">
                        <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                          {userData?.email || 'N/A'}
                        </p>
                        <label className="flex items-center space-x-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={formData.emailPublic}
                            onChange={(e) => handleInputChange('emailPublic', e.target.checked)}
                            className="rounded border-gray-600 bg-gray-700 text-green-500"
                          />
                          <span>Make email public on my profile</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Member Since</label>
                      <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                        {userData?.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {formData.emailPublic && userData?.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                        <div className="bg-gray-700 rounded px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-300">
                              {userData.email}
                            </span>
                            <span className="text-xs text-green-400 bg-green-900 px-2 py-1 rounded">
                              Public
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={formData.emailPublic && userData?.email ? "" : "md:col-span-2"}>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Member Since</label>
                      <p className="text-gray-300 bg-gray-700 rounded px-3 py-2">
                        {userData?.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Message Host Modal */}
      {showMessageForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Message {userData?.fullName || 'Host'}</h3>
              <button
                onClick={() => setShowMessageForm(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Subject</label>
                <input
                  type="text"
                  value={messageData.subject}
                  onChange={(e) => setMessageData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="Booking inquiry, question, etc."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                <textarea
                  value={messageData.message}
                  onChange={(e) => setMessageData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white h-32 resize-none"
                  placeholder="Tell the host about your event, ask questions, or discuss booking details..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Your Email (for response)</label>
                <input
                  type="email"
                  value={messageData.contactEmail}
                  onChange={(e) => setMessageData(prev => ({ ...prev, contactEmail: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Your Phone (optional)</label>
                <input
                  type="tel"
                  value={messageData.contactPhone}
                  onChange={(e) => setMessageData(prev => ({ ...prev, contactPhone: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSendMessage}
                disabled={sendingMessage || !messageData.subject || !messageData.message || !messageData.contactEmail}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded transition-colors duration-200"
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
              <button
                onClick={() => setShowMessageForm(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </HostLayout>
  );
};

export default HostProfile;
